<template>
  <div class="booking-rodo" v-if="dataReady">
    <Headline :value="`<b>Potwierdź</b> oświadczenia`" :number="5" />

    <div class="booking-rodo__wrapper">
      <div class="booking-rodo__body">
        <div class="booking-rodo__body--item">
          <CheckBox class="" v-model="allSelected" @input="toggle" />
          <CheckBoxLabel class="label-all" v-model="allSelected" @input="toggle"
            :enabled="true" :html="true" text="<b>Zaznacz wszystkie zgody</b>" tabindex="0" />
        </div>

        <RodoFormAgreement v-for="item in checklist" :key="item.id"
          @input="input($event, item)" v-model="item.selected" :item="item"
          :required="item.required"
          :name="`rodoStatement_${item.id + 1}`" :vvName="`rodoStatement_${item.id + 1}`" :vvKey="`rodoStatement_${item.id + 1}`"
          :validateAs="`rodoStatement_${item.id + 1}`" :rules="{ required: item.required }" />
      </div>

      <RodoRegulations class="booking-rodo__regulations"
        :preamble="regulationsPreamble" :body="regulationsBody" />
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import Component from 'vue-class-component'
import { mapActions, mapGetters } from 'vuex'

import RodoFormLogic from '@/components/booking/logic/RodoForm'
import RodoFormAgreement from '@/components/booking/RodoFormAgreement.vue'
import CheckBox from '@/components/CheckBox.vue'
import CheckBoxLabel from '@/components/CheckBoxLabel.vue'
import DataReadyMixin from '@/components/DataReadyMixin'
import Headline from '@/components/forms/Headline'
import RodoRegulations from '@/components/forms/RodoRegulations.vue'
import * as events from '@/events'
import { API } from '@/modules/api'
import Store from '@/store/index'
import { SET_RODO_STATEMENTS, SET_RODO_STATEMENT } from '@/store/mutation-types'
import FormStorageMixin from '@/mixins/FormStorageMixin'

@Component({
  store: Store,
  mixins: [
    DataReadyMixin,
    RodoFormLogic,
    FormStorageMixin,
  ],
  components: {
    Headline,
    CheckBox,
    CheckBoxLabel,
    RodoFormAgreement,
    RodoRegulations,
  },
  props: {
    form: {
      type: HTMLFormElement,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      user: 'Booking/getUser',
    }),
    enabledCount () {
      return this.rodo.checklist.filter(option => option.enabled).length
    },
    selectedCount () {
      return this.rodo.checklist.filter(option => option.selected).length
    },
  },
  watch: {
    checklist: {
      handler (newVal) {
        if (!newVal || !Array.isArray(newVal)) return

        const currentData = this.loadFromStorage() || {}
        this.saveToStorage({
          ...currentData,
          rodoFormAgreements: {
            allSelected: this.allSelected,
            checklist: newVal.map(item => ({
              id: item.id,
              selected: item.selected,
              required: item.required,
            })),
          },
        })
      },
      deep: true,
      immediate: true,
    },
    allSelected: {
      handler (newVal) {
        const currentData = this.loadFromStorage() || {}
        this.saveToStorage({
          ...currentData,
          rodoFormAgreements: {
            ...currentData.rodoFormAgreements,
            allSelected: newVal,
          },
        })
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      setRodoStatements: `Booking/${SET_RODO_STATEMENTS}`,
      setRodoStatement: `Booking/${SET_RODO_STATEMENT}`,
    }),
  },
})
export default class RodoForm extends Vue {
  dataReady = true
  allSelected = false
  regulationsPreamble = ''
  regulationsBody = []

  get checklist () {
    return this.rodo.checklist
  }

  created () {
    new API().getRodoRegulationsContent()
      .then(this.setRegulationsData)
      .then(this.markDataAsReady)
      .then(this.loadStoredData)
      .then(this.bindUserDataWithRodoForm)
  }

  loadStoredData () {
    const storedData = this.loadFromStorage()
    if (!storedData || !storedData.rodoFormAgreements) return

    if (storedData.rodoFormAgreements.checklist) {
      this.setRodoStatements(storedData.rodoFormAgreements.checklist)

      storedData.rodoFormAgreements.checklist.forEach(item => {
        const existingItem = this.checklist.find(i => i.id === item.id)
        if (existingItem) {
          Vue.set(existingItem, 'selected', item.selected)
        }
      })
    }

    this.allSelected = storedData.rodoFormAgreements.allSelected || false
  }

  bindUserDataWithRodoForm () {
    events.bookingOrderCreate.subscribe(() => {
      log.debug('Copying over data from order form to rodo form')
      this.setRodoUser()
      this.setRodoAgreements()

      const currentData = this.loadFromStorage() || {}
      delete currentData.rodoFormAgreements
      this.saveToStorage(currentData)

      log.debug('Submitting RODO form', this.form)
      this.form.submitWFFM()
    })
  }

  setRegulationsData ({ preamble, body }) {
    this.regulationsPreamble = preamble
    this.regulationsBody = body
  }

  input (selected, item) {
    this.setRodoStatement({ id: item.id, value: selected })

    Vue.set(item, 'selected', selected)

    const currentData = this.loadFromStorage() || {}
    this.saveToStorage({
      ...currentData,
      rodoFormAgreements: {
        allSelected: this.allSelected,
        checklist: this.checklist.map(checklistItem => ({
          id: checklistItem.id,
          selected: checklistItem.id === item.id ? selected : checklistItem.selected,
          required: checklistItem.required,
        })),
      },
    })
  }

  toggle () {
    if (this.allSelected) {
      this.selectAll()
    } else {
      this.deselectAll()
    }

    // Update localStorage
    const currentData = this.loadFromStorage() || {}
    this.saveToStorage({
      ...currentData,
      rodoFormAgreements: {
        allSelected: this.allSelected,
        checklist: this.checklist.map(item => ({
          id: item.id,
          selected: this.allSelected,
          required: item.required,
        })),
      },
    })
  }

  selectAll () {
    this.checklist.forEach(item => {
      Vue.set(item, 'selected', true)
      this.setRodoStatement({ id: item.id, value: true })
    })
  }

  deselectAll () {
    this.checklist.forEach(item => { Vue.set(item, 'selected', false) })
    this.setRodoStatements(this.checklist)
  }

  checklistUpdate () {
    const currentData = this.loadFromStorage()
    if (currentData && currentData.rodoFormAgreements) {
      this.checklist.forEach(item => {
        const storedItem = currentData.rodoFormAgreements.checklist.find(
          stored => stored.id === item.id
        )
        if (storedItem) {
          Vue.set(item, 'selected', storedItem.selected)
          this.setRodoStatement({ id: item.id, value: storedItem.selected })
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-rodo {
  padding: 0 20px;

  @include mediaFluid(tablet) {
    padding: 0;
  }

  &__wrapper {
    background: #fff;
    padding: 20px;
  }

  &__body--item {
    display: flex;
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .label-all {
    font-size: 12px;
    text-transform: uppercase;
    color: #005CA9;

    &.checkbox-label--selected {
      color: #005CA9;
    }
  }

  .checkbox {
    margin-right: 5px;
    background: transparent;
    border: 2px solid #005CA9;
    color: transparent;

    &::before {
      top: -6px;
      left: 2px;
    }

    &--checked {
      color: #005CA9;
    }
  }
}
</style>

<style lang="scss">
.booking-rodo {
  &__regulations {
    margin-top: 25px;

    p {
      font-size: 10px;
    }

    button {
      margin-top: 25px;
      font-family: 'Gotham';
      font-size: 12px;
      font-weight: bold;
      text-transform: uppercase;
      text-decoration-color: rgba(0,92,169,.5);
      color: #005CA9;
    }

    ol {
      padding: 0 0 0 10px;

      li {
        padding-left: 10px;
      }
    }
  }

  .headline {
    font-family: 'Gotham Book';

    span {
      line-height: 1.2;
      font-size: 1.3em;
      font-weight: bold;

      @include mediaFluid(tablet) {
        line-height: 1;
        font-size: 22px;
        font-weight: lighter;
      } 
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/booking/RodoForm.vue